import { useEffect, useState } from 'react';

import { getSaasParams, updateSaasParams } from '@/lib/api/saasApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useSaasStore } from '@/stores/saas-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CreditCard, Gift, Globe, Save, ToggleLeft, Users } from 'lucide-react';
import { toast } from 'sonner';

// ─── Toggle Switch ─────────────────────────────────────────────

interface ToggleProps {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  colorOn?: string;
}

function Toggle({ checked, onChange, disabled = false, colorOn = 'bg-emerald-600' }: ToggleProps) {
  return (
    <button
      type='button'
      role='switch'
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? colorOn : 'bg-slate-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ─── Panel ─────────────────────────────────────────────────────

export default function SaasParamsPanel() {
  const queryClient = useQueryClient();
  const setParams = useSaasStore(s => s.setParams);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.saasParams(),
    queryFn: getSaasParams,
  });

  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMessage(data.maintenanceMessage ?? '');
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: updateSaasParams,
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: queryKeys.saasParams() });
      setParams({
        maintenanceModeFront: result.maintenanceModeFront,
        maintenanceModeAdmin: result.maintenanceModeAdmin,
        openRegister: result.openRegister,
        openCompetitionRegistration: result.openCompetitionRegistration,
        referralEnabled: result.referralEnabled,
        playerPlusEnabled: result.playerPlusEnabled,
        maintenanceMessage: result.maintenanceMessage,
      });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Erreur lors de la mise a jour');
    },
  });

  function handleToggle(
    field:
      | 'maintenanceModeFront'
      | 'maintenanceModeAdmin'
      | 'openRegister'
      | 'openCompetitionRegistration'
      | 'referralEnabled'
      | 'playerPlusEnabled',
    value: boolean,
  ) {
    mutation.mutate(
      { [field]: value },
      {
        onSuccess: () => {
          toast.success('Parametre mis a jour');
        },
      },
    );
  }

  function handleSaveMessage() {
    mutation.mutate(
      { maintenanceMessage: message.trim() || null },
      {
        onSuccess: () => {
          toast.success('Message de maintenance enregistre');
        },
      },
    );
  }

  const params = data;
  const busy = mutation.isPending;

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-48'>
        <div className='w-8 h-8 border-4 border-red-200 border-t-red-600 rounded-full animate-spin' />
      </div>
    );
  }

  return (
    <div className='space-y-6 max-w-2xl'>
      {/* ── Maintenance ─────────────────────────────────────── */}
      <div className='bg-white rounded-xl border border-slate-200 p-6'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center'>
            <AlertTriangle className='w-5 h-5 text-amber-600' />
          </div>
          <div>
            <h2 className='text-lg font-bold text-slate-900'>Modes maintenance</h2>
            <p className='text-sm text-slate-500'>Bloquer l&apos;acces aux utilisateurs non-admin</p>
          </div>
        </div>

        <div className='space-y-5'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-semibold text-slate-700'>Mode maintenance — Site public</p>
              <p className='text-xs text-slate-500 mt-0.5'>Affiche une page de maintenance sur le site public</p>
            </div>
            <Toggle
              checked={params?.maintenanceModeFront ?? false}
              onChange={v => handleToggle('maintenanceModeFront', v)}
              disabled={busy}
              colorOn='bg-amber-500'
            />
          </div>

          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-semibold text-slate-700'>Mode maintenance — Backoffice admin</p>
              <p className='text-xs text-slate-500 mt-0.5'>
                Redirige tous les non-superadmins vers la page de maintenance
              </p>
            </div>
            <Toggle
              checked={params?.maintenanceModeAdmin ?? false}
              onChange={v => handleToggle('maintenanceModeAdmin', v)}
              disabled={busy}
              colorOn='bg-red-600'
            />
          </div>

          {params?.maintenanceModeAdmin && (
            <div className='bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2'>
              <AlertTriangle className='w-4 h-4 text-red-600 mt-0.5 flex-shrink-0' />
              <p className='text-xs text-red-700'>
                Le mode maintenance admin est actif. Seuls les super-admins peuvent acceder au backoffice.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Inscriptions ────────────────────────────────────── */}
      <div className='bg-white rounded-xl border border-slate-200 p-6'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center'>
            <Users className='w-5 h-5 text-blue-600' />
          </div>
          <div>
            <h2 className='text-lg font-bold text-slate-900'>Inscriptions</h2>
            <p className='text-sm text-slate-500'>Controle des inscriptions a la plateforme</p>
          </div>
        </div>

        <div className='space-y-5'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-semibold text-slate-700'>Inscriptions plateforme admin</p>
              <p className='text-xs text-slate-500 mt-0.5'>
                Autoriser les nouveaux utilisateurs a s&apos;inscrire sur la plateforme
              </p>
            </div>
            <Toggle
              checked={params?.openRegister ?? true}
              onChange={v => handleToggle('openRegister', v)}
              disabled={busy}
            />
          </div>

          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-semibold text-slate-700'>Inscriptions aux concours</p>
              <p className='text-xs text-slate-500 mt-0.5'>
                Autoriser les joueurs a s&apos;inscrire aux concours sur le site public
              </p>
            </div>
            <Toggle
              checked={params?.openCompetitionRegistration ?? true}
              onChange={v => handleToggle('openCompetitionRegistration', v)}
              disabled={busy}
            />
          </div>
        </div>
      </div>

      {/* ── Parrainage ────────────────────────────────────────── */}
      <div className='bg-white rounded-xl border border-slate-200 p-6'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center'>
            <Gift className='w-5 h-5 text-indigo-600' />
          </div>
          <div>
            <h2 className='text-lg font-bold text-slate-900'>Parrainage</h2>
            <p className='text-sm text-slate-500'>Activer ou desactiver le systeme de parrainage</p>
          </div>
        </div>

        <div className='space-y-5'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-semibold text-slate-700'>Systeme de parrainage</p>
              <p className='text-xs text-slate-500 mt-0.5'>
                Affiche / Masque les pages parrainage, les codes et les onglets parrainage dans les parametres
                utilisateurs
              </p>
            </div>
            <Toggle
              checked={params?.referralEnabled ?? true}
              onChange={v => handleToggle('referralEnabled', v)}
              disabled={busy}
            />
          </div>
        </div>
      </div>

      {/* ── Abonnement Joueur Plus ─────────────────────────── */}
      <div className='bg-white rounded-xl border border-slate-200 p-6'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center'>
            <CreditCard className='w-5 h-5 text-orange-600' />
          </div>
          <div>
            <h2 className='text-lg font-bold text-slate-900'>Abonnement Joueur Plus</h2>
            <p className='text-sm text-slate-500'>Activer ou desactiver l&apos;offre d&apos;abonnement joueur</p>
          </div>
        </div>

        <div className='space-y-5'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-semibold text-slate-700'>Offre Joueur Plus</p>
              <p className='text-xs text-slate-500 mt-0.5'>
                Masque les boutons de souscription, affiche &quot;Bientot disponible&quot; sur la page tarifs et dans
                les parametres joueur
              </p>
            </div>
            <Toggle
              checked={params?.playerPlusEnabled ?? false}
              onChange={v => handleToggle('playerPlusEnabled', v)}
              disabled={busy}
            />
          </div>
        </div>
      </div>

      {/* ── Message de maintenance ───────────────────────────── */}
      <div className='bg-white rounded-xl border border-slate-200 p-6'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center'>
            <Globe className='w-5 h-5 text-slate-600' />
          </div>
          <div>
            <h2 className='text-lg font-bold text-slate-900'>Message de maintenance</h2>
            <p className='text-sm text-slate-500'>Affiche sur les pages de maintenance (optionnel)</p>
          </div>
        </div>

        <div className='space-y-4'>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder='Ex : Maintenance planifiee de 14h a 16h. Merci de votre patience.'
            rows={3}
            className='w-full px-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:border-red-500 focus:outline-none resize-none'
          />
          <div className='flex items-center justify-between'>
            <p className='text-xs text-slate-400'>
              {message.length}/500 caracteres — laisser vide pour masquer le message
            </p>
            <button
              type='button'
              onClick={handleSaveMessage}
              disabled={busy || message.length > 500}
              className='flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <Save className='w-4 h-4' />
              Enregistrer
            </button>
          </div>
        </div>
      </div>

      {/* ── Infos ─────────────────────────────────────────────── */}
      <div className='flex items-center gap-2 text-xs text-slate-400'>
        <ToggleLeft className='w-4 h-4' />
        <span>
          Les toggles sont appliques immediatement. Derniere mise a jour :{' '}
          {params?.updatedAt
            ? new Date(params.updatedAt).toLocaleString('fr-FR', {
                dateStyle: 'short',
                timeStyle: 'short',
              })
            : '—'}
        </span>
      </div>
    </div>
  );
}
